import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useThree } from '@react-three/fiber'
import { Group } from 'three'
import { Container, Text } from '@react-three/uikit'
import { useLibrary } from '../data/useLibrary'
import { useInstances } from '../scene/instances'
import { headPose, menuPose, spawnPoseInFront } from '../scene/spawn'

interface HubMenuProps {
  open: boolean
  onClose(): void
  onExit(): void
}

function Btn({
  label,
  onClick,
  color = '#262626',
  hover = '#3f3f46',
  grow,
}: {
  label: ReactNode
  onClick(): void
  color?: string
  hover?: string
  grow?: boolean
}) {
  return (
    <Container
      flexGrow={grow ? 1 : 0}
      paddingX={12}
      paddingY={8}
      borderRadius={10}
      backgroundColor={color}
      hover={{ backgroundColor: hover }}
      onClick={onClick}
    >
      <Text fontSize={15} color="#fafafa">
        {label}
      </Text>
    </Container>
  )
}

export function HubMenu({ open, onClose, onExit }: HubMenuProps) {
  const groupRef = useRef<Group>(null)
  const camera = useThree((s) => s.camera)
  const { models, refresh } = useLibrary()
  const list = useInstances((s) => s.list)
  const spawn = useInstances((s) => s.spawn)
  const duplicate = useInstances((s) => s.duplicate)
  const remove = useInstances((s) => s.remove)
  const select = useInstances((s) => s.select)
  const [tab, setTab] = useState<'library' | 'instances'>('library')

  useEffect(() => {
    if (!open || groupRef.current == null) return
    void refresh() // lobby imports happen after this menu mounted — re-read the library each open
    const pose = menuPose(headPose(camera))
    groupRef.current.position.fromArray(pose.position)
    groupRef.current.quaternion.fromArray(pose.quaternion)
  }, [open, camera, refresh])

  if (!open) return null

  const nameOf = (assetId: string) => models.find((m) => m.id === assetId)?.name ?? 'model'

  const handleSpawn = (assetId: string) => {
    spawn(assetId, spawnPoseInFront(headPose(camera)))
    onClose()
  }

  const handleEdit = (instanceId: string) => {
    select(instanceId)
    onClose()
  }

  return (
    <group ref={groupRef}>
      <Container
        flexDirection="column"
        width={420}
        padding={16}
        gap={10}
        backgroundColor="#171717"
        borderRadius={16}
        pixelSize={0.0015}
      >
        <Container flexDirection="row" gap={8}>
          <Btn
            grow
            label="Library"
            color={tab === 'library' ? '#5b21b6' : '#262626'}
            hover={tab === 'library' ? '#6d28d9' : '#3f3f46'}
            onClick={() => setTab('library')}
          />
          <Btn
            grow
            label={`Spawned (${list.length})`}
            color={tab === 'instances' ? '#5b21b6' : '#262626'}
            hover={tab === 'instances' ? '#6d28d9' : '#3f3f46'}
            onClick={() => setTab('instances')}
          />
        </Container>

        {tab === 'library' &&
          (models.length === 0 ? (
            <Text fontSize={14} color="#a3a3a3">
              Import models in the lobby
            </Text>
          ) : (
            models.map((m) => (
              <Container
                key={m.id}
                padding={10}
                borderRadius={10}
                backgroundColor="#262626"
                hover={{ backgroundColor: '#3f3f46' }}
                onClick={() => handleSpawn(m.id)}
              >
                <Text fontSize={16} color="#fafafa">
                  {m.name}
                </Text>
              </Container>
            ))
          ))}

        {tab === 'instances' &&
          (list.length === 0 ? (
            <Text fontSize={14} color="#a3a3a3">
              Nothing spawned yet
            </Text>
          ) : (
            list.map((i, n) => (
              <Container key={i.instanceId} flexDirection="row" alignItems="center" gap={8}>
                <Container flexGrow={1}>
                  <Text fontSize={15} color="#fafafa">
                    {`${n + 1}. ${nameOf(i.assetId)}`}
                  </Text>
                </Container>
                <Btn label="Edit" onClick={() => handleEdit(i.instanceId)} />
                <Btn label="Dup" onClick={() => duplicate(i.instanceId)} />
                <Btn
                  label="Del"
                  color="#7f1d1d"
                  hover="#991b1b"
                  onClick={() => remove(i.instanceId)}
                />
              </Container>
            ))
          ))}

        <Btn label="Exit to lobby" color="#5b21b6" hover="#6d28d9" onClick={onExit} />
      </Container>
    </group>
  )
}
